import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, Save, X, Loader2 } from 'lucide-react';

/**
 * Ürün kategorileri
 */
const CATEGORIES = [
  'Getränke',
  'Fleisch',
  'Fisch',
  'Gemüse',
  'Obst',
  'Milchprodukte',
  'Gewürze',
  'Saucen',
  'Öle',
  'Tiefkühl',
  'Trockenwaren',
  'Verpackung',
  'Reinigung',
  'Sonstiges',
];

/**
 * Mengeneinheiten
 */
const UNITS = [
  'Stück',
  'kg',
  'g',
  'Liter',
  'ml',
  'Packung',
  'Karton',
  'Palette',
  'Dose',
  'Flasche',
  'Beutel',
];

/**
 * Product Form Component
 * Ürün ekleme/düzenleme formu
 *
 * @param {Object} product - Düzenlenecek ürün (null = yeni ürün)
 * @param {function} onSubmit - Form gönderildiğinde
 * @param {function} onCancel - İptal edildiğinde
 * @param {boolean} isLoading - Yükleniyor durumu
 */
export default function ProductForm({ product, onSubmit, onCancel, isLoading = false }) {
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: '',
    description: '',
    price: '',
    unit: 'Stück',
    min_order_quantity: '1',
    stock_status: 'In Stock',
    is_active: true,
    image_url: '',
    lagerort: '',
    stock: '0',
    min_stock: '0',
    melde_bestand: '20',
    hoechst_bestand: '100',
  });

  const [variants, setVariants] = useState([]);
  const [errors, setErrors] = useState({});

  // Düzenleme modunda formu doldur
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        sku: product.sku || '',
        category: product.category || '',
        description: product.description || '',
        price: product.price?.toString() || '',
        unit: product.unit || 'Stück',
        min_order_quantity: product.min_order_quantity?.toString() || '1',
        stock_status: product.stock_status || 'In Stock',
        is_active: product.is_active !== false,
        image_url: product.image_url || '',
        lagerort: product.lagerort || '',
        stock: product.stock?.toString() || '0',
        min_stock: product.min_stock?.toString() || '0',
        melde_bestand: product.melde_bestand?.toString() || '20',
        hoechst_bestand: product.hoechst_bestand?.toString() || '100',
      });

      let parsedVariants = product.variants || [];
      if (typeof parsedVariants === 'string') {
        try { parsedVariants = JSON.parse(parsedVariants); } catch { parsedVariants = []; }
      }
      setVariants(parsedVariants);
    }
  }, [product]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Hata temizle
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleAddVariant = () => {
    setVariants([...variants, { name: '', sku: '', price: formData.price, stock: 0 }]);
  };

  const handleRemoveVariant = (index) => {
    setVariants(variants.filter((_, i) => i !== index));
  };

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setVariants(newVariants);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Produktname ist erforderlich';
    }
    if (!formData.sku.trim()) {
      newErrors.sku = 'Artikelnummer ist erforderlich';
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Gültiger Preis ist erforderlich';
    }
    if (!formData.category) {
      newErrors.category = 'Kategorie ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    const submitData = {
      ...formData,
      price: parseFloat(formData.price),
      min_order_quantity: parseInt(formData.min_order_quantity) || 1,
      min_stock: parseInt(formData.min_stock) || 0,
      melde_bestand: parseInt(formData.melde_bestand) || 0,
      hoechst_bestand: parseInt(formData.hoechst_bestand) || 0,
    };

    if (isEditing) {
      // Mevcut üründe stok ve varyantlar bu formdan YAZILMAZ — bayat form
      // değerleri eşzamanlı stok hareketlerini (satış/mal kabul) ezmesin.
      // Stok yalnızca stok motoru (Lager/Inventur/Warenausgang) ile değişir.
      delete submitData.stock;
      delete submitData.stock_status;
    } else {
      submitData.stock = parseInt(formData.stock) || 0;
      submitData.variants = variants;
    }

    onSubmit(submitData);
  };

  const generateSKU = () => {
    const prefix = formData.category ? formData.category.substring(0, 3).toUpperCase() : 'PRD';
    const random = Math.random().toString(36).substr(2, 6).toUpperCase();
    handleChange('sku', `${prefix}-${random}`);
  };

  const isEditing = !!product?.id;

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <Package className="w-5 h-5 text-slate-600" />
          {isEditing ? 'Produkt bearbeiten' : 'Neues Produkt'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="name">Produktname *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="z.B. Tomatensoße 500ml"
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
            </div>

            {/* Image URL */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="image_url">Bild-URL</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => handleChange('image_url', e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            {/* SKU */}
            <div className="space-y-2">
              <Label htmlFor="sku">Artikelnummer (SKU) *</Label>
              <div className="flex gap-2">
                <Input
                  id="sku"
                  value={formData.sku}
                  onChange={(e) => handleChange('sku', e.target.value.toUpperCase())}
                  placeholder="z.B. SOS-ABC123"
                  className={errors.sku ? 'border-red-500' : ''}
                />
                <Button type="button" variant="outline" onClick={generateSKU}>
                  Auto
                </Button>
              </div>
              {errors.sku && <p className="text-xs text-red-500">{errors.sku}</p>}
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">Kategorie *</Label>
              <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                <SelectTrigger className={errors.category ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Kategorie wählen" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.category && <p className="text-xs text-red-500">{errors.category}</p>}
            </div>

            {/* Price */}
            <div className="space-y-2">
              <Label htmlFor="price">Preis (€) *</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => handleChange('price', e.target.value)}
                placeholder="0.00"
                className={errors.price ? 'border-red-500' : ''}
              />
              {errors.price && <p className="text-xs text-red-500">{errors.price}</p>}
            </div>

            {/* Unit */}
            <div className="space-y-2">
              <Label htmlFor="unit">Einheit</Label>
              <Select value={formData.unit} onValueChange={(v) => handleChange('unit', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Einheit wählen" />
                </SelectTrigger>
                <SelectContent>
                  {UNITS.map(unit => (
                    <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Stock — mevcut üründe stok motoru yönetir, form kilitli */}
            <div className="space-y-2">
              <Label htmlFor="stock">Bestand</Label>
              <Input
                id="stock"
                type="number"
                min="0"
                value={formData.stock}
                onChange={(e) => handleChange('stock', e.target.value)}
                disabled={isEditing}
                title={isEditing ? 'Bestand wird über Lager / Inventur / Warenausgang gebucht' : undefined}
              />
              {isEditing && (
                <p className="text-[10px] text-blue-500 font-medium">Bestand wird über Lager / Inventur gebucht</p>
              )}
            </div>

            {/* Min Stock */}
            <div className="space-y-2">
              <Label htmlFor="min_stock">Mindestbestand</Label>
              <Input
                id="min_stock"
                type="number"
                min="0"
                value={formData.min_stock}
                onChange={(e) => handleChange('min_stock', e.target.value)}
              />
            </div>

            {/* Meldebestand (Dispo) */}
            <div className="space-y-2">
              <Label htmlFor="melde_bestand">Meldebestand</Label>
              <Input
                id="melde_bestand"
                type="number"
                min="0"
                value={formData.melde_bestand}
                onChange={(e) => handleChange('melde_bestand', e.target.value)}
              />
            </div>

            {/* Höchstbestand (Dispo) */}
            <div className="space-y-2">
              <Label htmlFor="hoechst_bestand">Höchstbestand</Label>
              <Input
                id="hoechst_bestand"
                type="number"
                min="0"
                value={formData.hoechst_bestand}
                onChange={(e) => handleChange('hoechst_bestand', e.target.value)}
              />
            </div>

            {/* Lagerort */}
            <div className="space-y-2">
              <Label htmlFor="lagerort">Lagerort</Label>
              <Input
                id="lagerort"
                value={formData.lagerort}
                onChange={(e) => handleChange('lagerort', e.target.value)}
                placeholder="z.B. Regal A-1"
              />
            </div>

            {/* Min Order Quantity */}
            <div className="space-y-2">
              <Label htmlFor="min_order_quantity">Mindestbestellmenge</Label>
              <Input
                id="min_order_quantity"
                type="number"
                min="1"
                value={formData.min_order_quantity}
                onChange={(e) => handleChange('min_order_quantity', e.target.value)}
              />
            </div>

            {/* Stock Status */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="stock_status">Lagerstatus</Label>
              <Select value={formData.stock_status} onValueChange={(v) => handleChange('stock_status', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="In Stock">Auf Lager</SelectItem>
                  <SelectItem value="Low Stock">Niedriger Bestand</SelectItem>
                  <SelectItem value="Out of Stock">Nicht verfügbar</SelectItem>
                  <SelectItem value="On Order">Bestellt</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Variants Editor */}
            <div className="space-y-4 md:col-span-2 border-t pt-4 mt-2">
              <div className="flex justify-between items-center">
                <Label className="text-base font-semibold">Varianten</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleAddVariant}>
                  Variante hinzufügen
                </Button>
              </div>

              {variants.length > 0 ? (
                <div className="space-y-3">
                  {variants.map((variant, index) => (
                    <div key={index} className="flex gap-2 items-end bg-slate-50 p-3 rounded-lg border">
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">Name</Label>
                        <Input
                          value={variant.name}
                          onChange={(e) => handleVariantChange(index, 'name', e.target.value)}
                          placeholder="z.B. XL / Blau"
                          className="h-8"
                        />
                      </div>
                      <div className="w-24 space-y-1">
                        <Label className="text-xs">SKU</Label>
                        <Input
                          value={variant.sku}
                          onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                          placeholder="SKU"
                          className="h-8"
                        />
                      </div>
                      <div className="w-20 space-y-1">
                        <Label className="text-xs">Bestand</Label>
                        <Input
                          type="number"
                          value={variant.stock}
                          onChange={(e) => handleVariantChange(index, 'stock', parseInt(e.target.value) || 0)}
                          className="h-8"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveVariant(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic text-center py-2">Keine Varianten definiert.</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2 md:col-span-2 border-t pt-4">
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Produktbeschreibung..."
                rows={3}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1"
            >
              <X className="w-4 h-4 mr-2" />
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-slate-900 hover:bg-slate-800"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Wird gespeichert...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Aktualisieren' : 'Erstellen'}
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export { CATEGORIES, UNITS };
